import React, { useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import colors from "../../../theme/color/ColorTheme";
import fontSize from "../../../theme/fonts/FontTheme";
import ProductImage from "../../../assets/images/image/clothes/product-1.png";
import ProductImage2 from "../../../assets/images/image/clothes/product-2.png";
import { IoMdAdd } from "react-icons/io";
import { LuMinus } from "react-icons/lu";

const Wrapper = styled.div``;

const Container = styled.div`
  display: flex;
  color: black;
  position: relative;
  font-size: 14px;
  padding: 14px 0;
  justify-content: space-between;
  border-bottom: solid 0.4px ${colors.line};
  box-sizing: border-box;
`;

const Left = styled.div`
  display: flex;
  gap: 12px;
`;

const Image = styled.div`
  img {
    width: auto;
    height: auto;
    max-height: 140px;
    object-fit: contain;
  }
`;

const Price = styled.div`
  display: inline-flex;
  gap: 4px;
  align-items: center;
  height: fit-content;
  font-size: ${fontSize.s};
  color: ${colors.maroon};
  span {
    font-size: ${fontSize.xs};
  }
`;

const Details = styled.div`
  display: flex;
  gap: 12px;
  flex-direction: column;
`;

const Name = styled.span`
  font-size: ${fontSize.xs};
  text-transform: uppercase;
`;

const ColumnDiv = styled.div`
  display: flex;
  gap: 4px;
  flex-direction: column;
`;

const Color = styled.span`
  font-size: ${fontSize.xxs};
`;

const Sizes = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;
const Title = styled.span`
  font-size: ${fontSize.xxs};
`;
const SizeOption = styled.span`
  cursor: pointer;
  pointer-events: auto;
  font-size: ${fontSize.xxs};
  ${({ $active }) =>
    $active &&
    `  
      text-decoration: underline;
      text-underline-offset: 4px;
      text-decoration-thickness: 1px;
    `}
`;

const QuantityDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: ${colors.black};
  padding: 4px 8px;
  width: fit-content;
  border: 1px solid ${colors.border};

  @media (max-width: 1000px) {
    gap: 0;
    font-size: ${fontSize.xs};
    padding: 5px;
  }
`;

const Arrow = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  color: ${({ disabled }) => (disabled ? colors.grayShade : colors.black)};
  font-size: ${fontSize.xxs};
  border: none;
  background: none;
  padding: 4px;
  transition: all 0.3s ease;

  &:hover {
    color: ${({ disabled }) =>
      disabled ? colors.grayShade : colors.grayShade};
  }
`;

const QuantityNumber = styled.span`
  min-width: 24px;
  font-size: ${fontSize.xs};
  text-align: center;
`;

const ColorSelector = styled.div`
  display: flex;
  gap: 8px;
`;

const ColorBox = styled.button`
  width: 32px;
  height: 15px;
  background: ${({ $color }) => $color};
  border: none;
  cursor: pointer;
  padding: 0;
  box-sizing: border-box;

  ${({ $active, $color }) =>
    $active &&
    `
    box-shadow: inset 0 0 0 1.5px ${$color};
    background-clip: content-box;
    padding: 2.2px;
  `}
`;

const QUANTITY_LIMITS = {
  MIN: 1,
  MAX: 10,
};

const VARIANTS = [
  {
    id: "black",
    color: "#262727",
    label: "Black",
    image: ProductImage,
    sizes: [38, 39, 40, 41, 42, 43, 45],
  },
  {
    id: "blue",
    color: "#134873",
    label: "Blue",
    image: ProductImage2,
    sizes: [38, 39, 40, 41],
  },
];

function CartItemEditCard() {
  const [variantIndex, setVariantIndex] = useState(0);
  const [size, setSize] = useState(VARIANTS[0].sizes[0]);
  const [quantity, setQuantity] = useState(1);

  const activeVariant = useMemo(() => VARIANTS[variantIndex], [variantIndex]);


  const handleVariantChange = useCallback((index) => {
    const nextVariant = VARIANTS[index];
    setVariantIndex(index);

    // preserve size if available, otherwise fallback
    setSize((prev) =>
      nextVariant.sizes.includes(prev) ? prev : nextVariant.sizes[0]
    );
  }, []);

  const increment = useCallback(
    () => setQuantity((q) => Math.min(q + 1, QUANTITY_LIMITS.MAX)),
    []
  );

  const decrement = useCallback(
    () => setQuantity((q) => Math.max(q - 1, QUANTITY_LIMITS.MIN)),
    []
  );

  return (
    <Wrapper>
      <Container>
        <Left>
          <Image>
            <img
              src={activeVariant.image}
              alt={`${activeVariant.label} product`}
            />
          </Image>

          <Details>
            <Name>Two tone dress with green feathers</Name>

            {/* PRICE */}
            <Price>
              <span>USD</span>4000
            </Price>

            {/* COLOR */}
            <ColumnDiv>
              <Color>{activeVariant.label}</Color>
              <ColorSelector>
                {VARIANTS.map((variant, index) => (
                  <ColorBox
                    key={variant.id}
                    aria-label={`Select ${variant.label}`}
                    $color={variant.color}
                    $active={index === variantIndex}
                    onClick={() => handleVariantChange(index)}
                  />
                ))}
              </ColorSelector>
            </ColumnDiv>

            {/* SIZE */}
            <ColumnDiv>
              <Title>Size:</Title>
              <Sizes>
                {activeVariant.sizes.map((s) => (
                  <SizeOption
                    key={s}
                    $active={s === size}
                    onClick={() => setSize(s)}
                  >
                    {s}
                  </SizeOption>
                ))}
              </Sizes>
            </ColumnDiv>

            {/* QUANTITY */}
            <ColumnDiv>
              <Title>Qty:</Title>
              <QuantityDiv>
                <Arrow
                  onClick={decrement}
                  disabled={quantity === QUANTITY_LIMITS.MIN}
                  aria-label="Decrease quantity"
                >
                  <LuMinus />
                </Arrow>

                <QuantityNumber>{quantity}</QuantityNumber>

                <Arrow
                  onClick={increment}
                  disabled={quantity === QUANTITY_LIMITS.MAX}
                  aria-label="Increase quantity"
                >
                  <IoMdAdd />
                </Arrow>
              </QuantityDiv>
            </ColumnDiv>
          </Details>
        </Left>
      </Container>
    </Wrapper>
  );
}

export default CartItemEditCard;
